import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { C } from '../theme/tokens';

// Real EVE type IDs, each verified live against ESI
// (/universe/types/{id}/ returns the exact planet_type string used as the
// key here — the same strings sde_planets stores). CCP serves the icon art
// for any type ID from images.evetech.net, so celestials get their real
// in-game art with no local assets and no new dependency.
//
// "Planet (Scorched Barren)" is deliberately absent: it exists on exactly
// one planet in the entire universe and has no ESI-resolvable type ID, so
// it falls through to the no-icon path rather than borrowing Barren's art.
const PLANET_TYPE_IDS: Record<string, number> = {
  'Planet (Temperate)': 11,
  'Planet (Ice)': 12,
  'Planet (Gas)': 13,
  'Planet (Oceanic)': 2014,
  'Planet (Lava)': 2015,
  'Planet (Barren)': 2016,
  'Planet (Storm)': 2017,
  'Planet (Plasma)': 2063,
  'Planet (Shattered)': 30889,
};

/** A planet's real in-game icon, keyed off the `planet_type` string already
 * on every sde_planets row. Renders nothing for an unknown/absent type —
 * callers keep showing the type name as text either way, so a missing icon
 * degrades to exactly the old text-only display. */
const PlanetIcon: React.FC<{ planetType: string | null; size?: number }> = ({ planetType, size = 20 }) => {
  const typeId = planetType ? PLANET_TYPE_IDS[planetType] : undefined;
  if (!typeId) return null;

  return (
    <Tooltip title={planetType}>
      <Box
        component="img"
        src={`https://images.evetech.net/types/${typeId}/icon?size=64`}
        alt={planetType ?? ''}
        loading="lazy"
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: '2px',
          // A square icon chip, matching EVE's own inventory/overview icon
          // slots — not a circular avatar treatment.
          border: `1px solid ${C.hairlineBright}`,
        }}
      />
    </Tooltip>
  );
};

export default PlanetIcon;
